import React, { useEffect, useState } from "react";
import { Plus, X, Layers } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";
import {
  getSubjectsForGrade,
  getStrandsForGradeSubject,
  getSpecificsForGradeSubject,
  isCombinedGrade,
  expandGrades,
} from "../lib/curriculum";

/**
 * Multi-subject / multi-strand / multi-expectation picker for the
 * Generator form. Shows up under the main single-pick grid and lets
 * teachers layer in additional Ontario subjects (cross-curricular),
 * additional strands within any chosen subject, and additional
 * specific expectations spanning all those strands.
 *
 * Operates on the canonical reducer arrays: `subjects[]`, `strands[]`,
 * `expectations[]`. The primary subject (= subjects[0]) and primary
 * strand still drive the single-pick dropdowns above this card; this
 * picker only manages the *additional* layers on top.
 *
 * Caps for the expectation checklist are enforced visually (over-cap
 * checkboxes go disabled with a hint). Backend re-validates.
 */
export default function CrossCurricularPicker({
  state,
  dispatch,
  curriculum,
  expectationCap,
  columnSubjects = [],
  className = "",
}) {
  const [open, setOpen] = useState(false);

  const { form, subjects, strands, expectations } = state;
  const grade = form.grade || "";
  const combined = isCombinedGrade(grade);
  // Combined Grades (Sep 2026): when the two-column per-grade surface is
  // active, those grade subjects are managed there — Cross-Curricular
  // reverts to genuine OPTIONAL cross-curricular extras only. Empty array
  // (single grade / unit / summative) keeps the original behaviour intact.
  const _colSet = new Set(columnSubjects);
  const _columnsActive = columnSubjects.length > 0;

  // For a combined grade, tag subjects that live in only SOME of the
  // component grades (e.g. Social Studies → Gr 6; History/Geography → Gr 7)
  // so the teacher can tell which grade each subject belongs to. Subjects
  // shared by both grades (Math, Science, Language) get no tag.
  const subjGradeTag = (subj) => {
    if (!combined) return "";
    const gs = expandGrades(grade);
    const inGs = gs.filter((g) => (curriculum?.subjects_by_grade?.[g] || []).includes(subj));
    if (inGs.length === 0 || inGs.length === gs.length) return "";
    return "Gr " + inGs.map((g) => (g.match(/[4-8]/) || [""])[0]).join("/");
  };
  const SubjTag = ({ subject }) => {
    const t = subjGradeTag(subject);
    return t ? (
      <span className="ml-2 inline-block px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-[#1c2d3d] text-white align-middle">
        {t}
      </span>
    ) : null;
  };

  // Auto-expand for combined grades (where the grade-tagged expectations
  // live); collapse again when returning to a single grade. When the
  // two-column surface owns combined selection, stay collapsed (optional).
  useEffect(() => {
    setOpen(combined && !_columnsActive);
  }, [combined, _columnsActive]);

  // All Ontario subjects available for this grade (after the primary).
  const allSubjectsForGrade = curriculum
    ? getSubjectsForGrade(curriculum, grade)
    : [];

  // Subjects the teacher hasn't picked yet — candidates for the
  // "Add another subject" dropdown.
  const remainingSubjects = allSubjectsForGrade.filter(
    (s) => !subjects.includes(s)
  );

  // We only render this picker once the teacher has set grade + a
  // primary subject. Without those the multi-pick story doesn't make
  // sense.
  if (!grade || subjects.length === 0) return null;

  // Show the "additional subjects" controls once we have a primary
  // subject. Strand/expectation sections appear when at least one
  // strand has been picked across all the chosen subjects.
  const totalChecked = expectations.length;
  const overCap = expectationCap > 0 && totalChecked >= expectationCap;

  const addExtraSubject = (subj) => {
    if (!subj) return;
    dispatch({ type: "ADD_SUBJECT", subject: subj });
  };

  const removeExtraSubject = (subj) => {
    dispatch({ type: "REMOVE_SUBJECT", subject: subj });
  };

  const toggleStrand = (subj, code) => {
    dispatch({ type: "TOGGLE_STRAND", subject: subj, code });
  };

  const toggleExpectation = (subj, strand, code, grade) => {
    const g = grade || "";
    const isChecked = expectations.some(
      (e) => e.subject === subj && e.strand === strand && e.code === code && (e.grade || "") === g
    );
    if (!isChecked && overCap) return; // cap reached
    dispatch({ type: "TOGGLE_EXPECTATION", subject: subj, strand, code, grade });
  };

  // Build the "extra subjects" rows (everything after subjects[0]).
  const extraSubjects = _columnsActive
    ? subjects.filter((s) => !_colSet.has(s))
    : subjects.slice(1);

  return (
    <div
      data-testid="cross-curricular-picker"
      className={`mt-6 rounded-xl border border-[#96ded1]/50 bg-[#96ded1]/10 ${className}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left"
        data-testid="cross-curricular-toggle"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="h-7 w-7 rounded-md bg-white border border-[#96ded1] grid place-items-center shrink-0">
            <Layers className="h-3.5 w-3.5 text-[#1c2d3d]" />
          </span>
          <span className="font-extrabold text-[#1c2d3d] text-sm tracking-tight">
            Cross-curricular &amp; multi-strand coverage
          </span>
          <span className="hidden sm:inline ae-pill !py-0.5 !px-2 !text-[10px] !bg-white !border-[#96ded1]">
            {subjects.length} subj · {strands.length} strands ·{" "}
            {totalChecked} exp
            {expectationCap > 0 ? ` / ${expectationCap}` : ""}
          </span>
        </div>
        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#4a6984]">
          {open ? "Hide" : "Open"}
        </span>
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-5">
          {combined && !_columnsActive && (
            <div className="rounded-lg border border-[#1c2d3d]/15 bg-[#96ded1]/15 px-3.5 py-2.5 text-xs text-[#1c2d3d]" data-testid="cross-curricular-combined-hint">
              <span className="font-extrabold">Combined grade ({expandGrades(grade).map((g) => (g.match(/[4-8]/) || [""])[0]).join("/")}).</span>{" "}
              Some subjects are grade-specific — e.g. <strong>Social Studies</strong> is Grade{" "}
              {expandGrades(grade)[0]?.match(/[4-8]/)?.[0]}, while <strong>History</strong> and{" "}
              <strong>Geography</strong> are Grade {expandGrades(grade)[1]?.match(/[4-8]/)?.[0]}. Add each grade&apos;s
              subject here (they&apos;re tagged with the grade), then tick the strands and expectations you&apos;ll cover.
            </div>
          )}
          <p className="text-xs text-[#4a6984] leading-relaxed">
            Many Ontario lessons cover more than one expectation, and the
            Ministry encourages cross-curricular planning. Add additional
            subjects, tick the strands you'll weave in, and check off the
            specific expectations this lesson should address.
            {expectationCap > 0 && (
              <span className="block mt-1 text-[#1c2d3d] font-bold">
                Cap for this mode: {expectationCap} expectations total.
              </span>
            )}
          </p>

          {/* Extra subject rows */}
          <section>
            <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#4a6984]/70 mb-2">
              Additional subjects
            </p>
            {extraSubjects.length === 0 ? (
              <p
                className="text-xs text-[#4a6984]/75 italic mb-2"
                data-testid="cross-curricular-no-extras"
              >
                {_columnsActive ? (
                  <>Optional. Add a subject here to weave in extra cross-curricular coverage.</>
                ) : (
                  <>
                    No additional subjects yet. The primary subject is{" "}
                    <strong className="text-[#1c2d3d]">{subjects[0]}</strong>.
                  </>
                )}
              </p>
            ) : (
              <ul className="space-y-1.5 mb-2">
                {extraSubjects.map((subj) => (
                  <li
                    key={subj}
                    className="flex items-center justify-between gap-3 rounded-md bg-white border border-[#96ded1]/40 px-3 py-2"
                    data-testid={`cross-curricular-extra-${subj
                      .replace(/\s+/g, "-")
                      .toLowerCase()}`}
                  >
                    <span className="font-bold text-sm text-[#1c2d3d]">
                      {subj}<SubjTag subject={subj} />
                    </span>
                    <button
                      type="button"
                      onClick={() => removeExtraSubject(subj)}
                      className="text-[#4a6984] hover:text-[#1c2d3d]"
                      aria-label={`Remove ${subj}`}
                      data-testid={`cross-curricular-remove-${subj
                        .replace(/\s+/g, "-")
                        .toLowerCase()}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {remainingSubjects.length > 0 ? (
              <Select
                value=""
                onValueChange={(v) => addExtraSubject(v)}
              >
                <SelectTrigger
                  data-testid="cross-curricular-add-subject"
                  className="bg-white border-[#96ded1]/60 text-[#1c2d3d] font-bold text-sm h-10 rounded-md"
                >
                  <span className="inline-flex items-center gap-2">
                    <Plus className="h-3.5 w-3.5" />
                    Add another subject
                  </span>
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {remainingSubjects.map((subj) => (
                    <SelectItem
                      key={subj}
                      value={subj}
                      data-testid={`cross-curricular-add-option-${subj
                        .replace(/\s+/g, "-")
                        .toLowerCase()}`}
                      className="text-[#1c2d3d] font-medium focus:bg-[#96ded1]/20"
                    >
                      {subj}{subjGradeTag(subj) ? ` · ${subjGradeTag(subj)}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-xs text-[#4a6984]/70">
                All curated subjects for {grade} are already in this lesson.
              </p>
            )}
          </section>

          {/* Strand checkboxes — grouped by subject */}
          <section>
            <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#4a6984]/70 mb-2">
              Strands to cover
            </p>
            <div className="space-y-3">
              {subjects.filter((s) => !_colSet.has(s)).map((subj) => {
                const subjStrands = curriculum
                  ? getStrandsForGradeSubject(curriculum, grade, subj)
                  : [];
                if (subjStrands.length === 0) return null;
                return (
                  <div
                    key={subj}
                    className="rounded-md bg-white border border-[#96ded1]/40 px-3.5 py-3"
                    data-testid={`strand-group-${subj
                      .replace(/\s+/g, "-")
                      .toLowerCase()}`}
                  >
                    <p className="font-extrabold text-xs text-[#1c2d3d] mb-2">
                      {subj}<SubjTag subject={subj} />
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {subjStrands.map((s) => {
                        const checked = strands.some(
                          (st) => st.subject === subj && st.code === s.code
                        );
                        const slug = `${subj}-${s.code}`
                          .replace(/\s+/g, "-")
                          .toLowerCase();
                        return (
                          <label
                            key={s.code}
                            className={`flex items-start gap-2 rounded px-2 py-1.5 cursor-pointer text-xs ${
                              checked
                                ? "bg-[#96ded1]/30 border border-[#96ded1]"
                                : "bg-[#f4f5f7]/60 border border-transparent hover:border-[#4a6984]/20"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleStrand(subj, s.code)}
                              data-testid={`strand-checkbox-${slug}`}
                              className="mt-0.5 h-3.5 w-3.5 rounded border-[#4a6984]/40 accent-[#1c2d3d] shrink-0"
                            />
                            <span className="min-w-0">
                              <span className="font-extrabold text-[#1c2d3d]">
                                {s.code}
                              </span>{" "}
                              <span className="text-[#4a6984]">{s.name}</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Expectation checklist — grouped by Subject → Strand */}
          {strands.filter(({ subject: subj }) => !_colSet.has(subj)).length > 0 && (
            <section>
              <div className="flex items-baseline justify-between gap-3 mb-2">
                <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#4a6984]/70">
                  Specific expectations to cover
                </p>
                <span
                  className={`text-[10px] font-extrabold uppercase tracking-[0.15em] ${
                    overCap ? "text-[#b45309]" : "text-[#1c2d3d]"
                  }`}
                  data-testid="cross-curricular-exp-count"
                >
                  {totalChecked}
                  {expectationCap > 0 ? ` of ${expectationCap}` : ""} ticked
                </span>
              </div>
              <div className="space-y-3 max-h-[36rem] overflow-y-auto pr-1">
                {strands.filter(({ subject: subj }) => !_colSet.has(subj)).map(({ subject: subj, code: strandCode }) => {
                  const specs = curriculum
                    ? getSpecificsForGradeSubject(curriculum, grade, subj)
                    : [];
                  const codes = specs.filter(
                    (r) => r.code && r.code.startsWith(strandCode)
                  );
                  if (codes.length === 0) return null;
                  const strandName =
                    (curriculum
                      ? getStrandsForGradeSubject(curriculum, grade, subj)
                      : []
                    ).find((s) => s.code === strandCode)?.name || strandCode;
                  return (
                    <div
                      key={`${subj}-${strandCode}`}
                      className="rounded-md bg-white border border-[#96ded1]/40 px-3.5 py-3"
                      data-testid={`exp-group-${subj
                        .replace(/\s+/g, "-")
                        .toLowerCase()}-${strandCode.toLowerCase()}`}
                    >
                      <p className="font-extrabold text-xs text-[#1c2d3d] mb-2">
                        {subj}<SubjTag subject={subj} /> ·{" "}
                        <span className="text-[#4a6984]">
                          {strandCode} — {strandName}
                        </span>
                      </p>
                      <ul className="space-y-1.5">
                        {codes.map((row) => {
                          const checked = expectations.some(
                            (e) =>
                              e.subject === subj &&
                              e.strand === strandCode &&
                              e.code === row.code &&
                              (e.grade || "") === (row.grade || "")
                          );
                          const disabled = overCap && !checked;
                          const slug = `${subj}-${row.code}${row.gradeNum ? `-${row.gradeNum}` : ""}`
                            .replace(/\s+/g, "-")
                            .toLowerCase();
                          return (
                            <li key={`${row.code}-${row.grade || ""}`}>
                              <label
                                className={`flex items-start gap-2.5 rounded px-2 py-1.5 transition-colors ${
                                  checked
                                    ? "bg-[#96ded1]/30 border border-[#96ded1]"
                                    : disabled
                                    ? "bg-[#f4f5f7]/40 border border-transparent opacity-50 cursor-not-allowed"
                                    : "bg-[#f4f5f7]/60 border border-transparent hover:border-[#4a6984]/20 cursor-pointer"
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={disabled}
                                  onChange={() =>
                                    toggleExpectation(
                                      subj,
                                      strandCode,
                                      row.code,
                                      row.grade
                                    )
                                  }
                                  data-testid={`exp-checkbox-${slug}`}
                                  className="mt-0.5 h-3.5 w-3.5 rounded border-[#4a6984]/40 accent-[#1c2d3d] shrink-0"
                                />
                                <span className="min-w-0 text-xs">
                                  <span className="font-extrabold text-[#1c2d3d]">
                                    {row.code}
                                  </span>
                                  {row.grade ? (
                                    <span className="ml-2 inline-block px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-[#1c2d3d] text-white align-middle">
                                      Gr {row.gradeNum}
                                    </span>
                                  ) : null}
                                  {row.topic ? (
                                    <span className="ml-2 text-[#4a6984] font-semibold">
                                      · {row.topic}
                                    </span>
                                  ) : null}
                                  {row.description ? (
                                    <span className="block text-[11px] text-[#4a6984] mt-0.5 leading-snug">
                                      {row.description}
                                    </span>
                                  ) : null}
                                </span>
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
